import { Component, ojs, app, payload, component } from "modular-openscriptjs";
import { Preferences } from "@capacitor/preferences";
import { Capacitor } from "@capacitor/core";
import { AndroidSmsRetriever } from "@capawesome/capacitor-android-sms-retriever";
import {
    pageInfo,
    processStart,
    processEnd,
    inform,
    redirect,
    store,
    otpVerifiedAtKey,
} from "../../helpers";
import { dom } from "../../fotastart";
import { api, PAGES, STRINGS, gc } from "../../declarations";

const h = app("h");

export default class OtpAuthPage extends Component {
    input = null;

    render(...args) {
        pageInfo(PAGES.OTP);

        return h.div(
            {
                class: " d-flex align-items-center justify-content-center py-5 px-3 py-md-5",
                style: "background: #f8fafc;",
            },

            h.style(
                {},
                `
				.otp-input-field {
					width: 52px;
					height: 62px;
					font-size: 1.8rem;
					font-weight: 700;
					border: 2px solid #dee2e6;
					border-radius: 12px;
					transition: all 0.2s ease-in-out;
					text-align: center;
					color: #212529;
					background: #ffffff;
				}
				.otp-input-field:focus {
					border-color: var(--bs-primary);
					box-shadow: 0 0 0 0.25rem rgba(var(--bs-primary-rgb, 13, 110, 253), 0.25);
					outline: 0;
				}
				.otp-input-field::-webkit-outer-spin-button,
				.otp-input-field::-webkit-inner-spin-button {
					-webkit-appearance: none;
					margin: 0;
				}
				.otp-input-field[type=number] {
					-moz-appearance: textfield;
				}
				.shield-icon-container {
					width: 70px;
					height: 70px;
					border-radius: 50%;
					background: #e7f1ff;
					display: flex;
					align-items: center;
					justify-content: center;
					color: #0d6efd;
					font-size: 2rem;
					margin: 0 auto 24px auto;
				}
			`,
            ),

            h.div(
                {
                    class: "card shadow border-0 text-center align-items-center p-4 p-md-5",
                    style: "max-width: 480px; width: 100%;",
                },

                h.div(
                    { class: "shield-icon-container" },
                    h.i({ class: "bi bi-shield-lock-fill" }),
                ),

                h.h3({ class: "fw-bold mb-2 text-dark" }, "Verification Code"),
                h.p(
                    { class: "text-muted mb-4 small px-2" },
                    "We have sent a One Time Password (OTP) to your phone and email. Please enter it below to verify your identity.",
                ),

                h.div(
                    { class: "w-100 d-flex flex-column gap-3 mb-4" },
                    h.div(
                        {
                            id: "oap-otp-input",
                            class: "d-flex justify-content-center gap-2",
                        },
                        h.call(() => {
                            let divs = [];

                            for (let i = 0; i < 5; i++) {
                                divs.push(
                                    h.input({
                                        type: "number",
                                        class: "otp-input-field",
                                        hook: "otp-input-" + i,
                                    }),
                                );
                            }

                            return divs;
                        }),
                    ),
                    h.div(
                        {
                            class: "d-flex justify-content-center align-items-center gap-2 mt-2",
                        },
                        h.span(
                            { class: "text-muted small" },
                            "Didn't receive code?",
                        ),
                        h.span(
                            {
                                class: "text-primary fw-semibold small text-decoration-none",
                                style: "cursor: pointer; transition: color 0.2s;",
                                onclick: this.method("resendCode"),
                            },
                            "Resend Code",
                        ),
                    ),
                ),

                h.div(
                    { class: "w-100" },
                    h.button(
                        {
                            class: "btn btn-success text-uppercase w-100 py-3 fw-bold rounded-3",
                            hook: "otp-verify-btn",
                            onclick: this.method("verifyOtp", ["${this}"]),
                        },
                        h.span({ class: "text" }, "Verify OTP"),
                    ),
                ),
            ),
            ...args,
        );
    }

    $_mounted(componentId) {
        let c = component(componentId);
        if (!c) return;
        c.input = new VanillaOTP(dom.id("oap-otp-input"));

        setTimeout(() => {
            dom.get("[hook=otp-input-0]")?.focus();
        }, 1000);

        // Use `c` (the resolved component), not `this` — inside
        // $_mounted, `this` is not bound to the component instance.
        c.autoFillOtpFromSms();
    }

    async resendCode() {
        processStart({ title: "Sending OTP" });
        let res = await api.post("/auth/send/otp");

        processEnd();
        if (!res) return;

        inform(res.message, STRINGS.SUCCESS);

        // A fresh OTP SMS is on its way, so start listening again.
        this.autoFillOtpFromSms();
    }

    /**
     * Listens for the incoming OTP SMS via the Android SMS Retriever API
     * and, once it arrives, fills the OTP inputs and submits automatically.
     * No SMS permissions are required — Google Play Services shows a
     * one-tap consent dialog before any message is read.
     *
     * Must be called on the resolved component instance (e.g. `c` from
     * `component(componentId)`, or `this` from a handler that is already
     * bound to the component) so that `this.input` / `this.verifyOtp`
     * resolve correctly.
     */
    async autoFillOtpFromSms() {
        if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== "android") {
            return;
        }

        try {
            const { message } = await AndroidSmsRetriever.retrieveSms();
            const code = message?.match(/\d{5}/)?.[0];

            if (!code || !this.input) return;

            this.input.setValue(code);

            const btn = dom.get("[hook=otp-verify-btn]");
            if (btn) this.verifyOtp(btn);
        } catch (error) {
            // Timed out, user declined the consent dialog, or no matching
            // SMS arrived — the user can still enter the code manually.
            console.warn("SMS auto-fill unavailable:", error);
        }
    }

    async verifyOtp(btn) {
        dom.disable(btn);
        let code = this.input?.getValue();

        processStart({ title: "Verifying OTP" });
        let res = await api.post("/auth/otp/verify", { code });
        processEnd();

        if (res) {
            const now = Date.now();
            window.otpVerifiedAt = now;

            // Scope the persisted verification to the current user's id
            // so it can never be read back as valid for a different
            // account signed in later on this device.
            await store(otpVerifiedAtKey(gc.user.id), String(now));

            Swal.fire({
                title: "OTP Verified",
                text: "We've verified your identity",
                icon: "success",
                timer: 1500,
                showConfirmButton: false,
                timerProgressBar: true,
            });

            setTimeout(async () => {
                const { value: url } = await Preferences.get({
                    key: "redirect-url-after-auth",
                });
                await Preferences.remove({ key: "redirect-url-after-auth" });
                if (url) {
                    if (url.startsWith("http")) {
                        window.location.href = url;
                    } else {
                        redirect().to(url);
                    }
                } else {
                    redirect().to("home");
                }
            }, 1000);
        }

        dom.enable(btn);
    }
}

ojs(OtpAuthPage);
